import { useEffect, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/lib/api";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const pad = (n) => String(n).padStart(2, "0");
export const isoOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
export const todayIso = () => {
  const d = new Date();
  return isoOf(d.getFullYear(), d.getMonth(), d.getDate());
};

function parse(iso) {
  if (!iso) return null;
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m - 1, 1);
}

/**
 * One month, Sunday first. In range mode the first click sets the start and
 * the second the end (hovering previews the range); in single mode a click
 * picks the day. Clicking the month title jumps to any month or year —
 * release dates go back years, and paging one month at a time would take
 * forever.
 */
export function Calendar({ mode = "single", value, from, to, onPick, initial }) {
  const start = parse(initial || (mode === "single" ? value : from || to)) || new Date();
  const [view, setView] = useState(new Date(start.getFullYear(), start.getMonth(), 1));
  const [picking, setPicking] = useState(false);
  const [hover, setHover] = useState(null);
  const y = view.getFullYear();
  const m = view.getMonth();
  const today = todayIso();

  const first = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells = Array(first).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  // While only a start is chosen, hovering shows where the range would end.
  const endGuess = mode === "range" && from && !to && hover && hover >= from ? hover : to;

  return (
    <div className="w-[308px] select-none">
      <div className="flex items-center justify-between mb-3 px-1">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => (picking ? setView(new Date(y - 1, m, 1)) : setView(new Date(y, m - 1, 1)))}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted2 hover:text-ink hover:bg-surface"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => setPicking((v) => !v)} className="font-display text-[16px] font-semibold text-ink px-2 py-1 rounded-lg hover:bg-surface" aria-label="Choose month and year">
          {picking ? y : view.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </button>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => (picking ? setView(new Date(y + 1, m, 1)) : setView(new Date(y, m + 1, 1)))}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted2 hover:text-ink hover:bg-surface"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {picking ? (
        <div className="grid grid-cols-3 gap-2 py-2">
          {MONTHS.map((label, i) => (
            <button
              type="button"
              key={label}
              onClick={() => {
                setView(new Date(y, i, 1));
                setPicking(false);
              }}
              className={cn("h-11 rounded-lg text-[13.5px] font-medium", i === m ? "bg-brand text-white" : "hover:bg-brand-light text-ink")}
            >
              {label}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 mb-1.5">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-center text-[11px] font-semibold tracking-wide text-muted2">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-1 gap-x-[2px]" onMouseLeave={() => setHover(null)}>
            {cells.map((d, i) => {
              if (!d) return <div key={i} className="h-10" />;
              const iso = isoOf(y, m, d);
              let cls = "text-ink hover:bg-surface rounded-lg";
              if (mode === "single") {
                if (iso === value) cls = "bg-brand text-white font-semibold rounded-lg";
              } else {
                const isFrom = iso === from;
                const isTo = iso === endGuess;
                const inside = from && endGuess && iso > from && iso < endGuess;
                if (isFrom && (isTo || !endGuess)) cls = "bg-brand text-white font-semibold rounded-lg";
                else if (isFrom) cls = "bg-brand text-white font-semibold rounded-l-lg";
                else if (isTo) cls = cn("font-semibold rounded-r-lg", to ? "bg-brand text-white" : "bg-brand/70 text-white");
                else if (inside) cls = "bg-brand-light text-ink";
              }
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => onPick(iso)}
                  onMouseEnter={() => setHover(iso)}
                  aria-label={formatDate(iso)}
                  aria-pressed={mode === "single" ? iso === value : iso === from || iso === to}
                  className={cn("h-10 text-[14px] flex items-center justify-center transition-colors relative", cls)}
                >
                  {d}
                  {iso === today && <span className="absolute bottom-1 w-1 h-1 rounded-full bg-current opacity-60" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** A date field that opens the calendar instead of the browser's own picker. */
export function DatePicker({ value, onChange, placeholder = "Pick a date", className, ariaLabel, clearable = true }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] flex items-center gap-2 text-left outline-none focus:border-brand focus:ring-2 focus:ring-brand/20",
            className
          )}
        >
          <CalendarDays className="w-4 h-4 text-muted2 shrink-0" />
          <span className={cn("flex-1 truncate", !value && "text-muted2/80")}>{value ? formatDate(value) : placeholder}</span>
          {value && clearable && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear date"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  onChange("");
                }
              }}
              className="p-0.5 rounded text-muted2 hover:text-ink"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} collisionPadding={16} className="w-auto p-4 rounded-2xl shadow-lift">
        <Calendar
          mode="single"
          value={value}
          onPick={(iso) => {
            onChange(iso);
            setOpen(false);
          }}
        />
        <div className="flex justify-between mt-3 pt-3 border-t border-line">
          <button
            type="button"
            onClick={() => {
              onChange(todayIso());
              setOpen(false);
            }}
            className="text-[12.5px] font-semibold text-brand"
          >
            Today
          </button>
          {clearable && value && (
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="text-[12.5px] text-muted2 hover:text-ink"
            >
              Clear
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Controlled helper for range picking: first click = from, second = to. */
export function useRangePick(value, onChange) {
  const [from, setFrom] = useState(value.from || "");
  const [to, setTo] = useState(value.to || "");
  useEffect(() => {
    setFrom(value.from || "");
    setTo(value.to || "");
  }, [value.from, value.to]);
  const pick = (iso) => {
    if (!from || (from && to) || iso < from) {
      setFrom(iso);
      setTo("");
      return;
    }
    setTo(iso);
    onChange({ ...value, preset: "custom", from, to: iso });
  };
  return { from, to, pick };
}
